import { Component, OnInit, inject } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { CaseManagementService } from '../../../case-management/case-management.service';
import { PostTradeService } from '../../post-trade.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Platform } from '@angular/cdk/platform';
import { saveAs } from '@progress/kendo-file-saver';
import { EnvironmentService } from '../../../services/environment.service';
import uniqBy from 'lodash-es/uniqBy';
import { DecimalPipe, Location } from '@angular/common';
import {
  AccountResult,
  TradeResponse,
} from '../../../models/post-trade-models';
import { DateTime } from 'luxon';
import { SessionStorageService } from '../../../services/session-storage.service';
import { NgxLoadingModule } from 'ngx-loading';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FormsModule } from '@angular/forms';
import { provideLuxonDateAdapter } from '@angular/material-luxon-adapter';

@Component({
  selector: 'app-attachments-dialog',
  templateUrl: './attachments-dialog.component.html',
  styleUrls: ['./attachments-dialog.component.scss'],
  imports: [
    NgxLoadingModule,
    DecimalPipe,
    MatDialogModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    FormsModule,
  ],
  providers: [provideLuxonDateAdapter()],
})
export class AttachmentsDialogComponent implements OnInit {
  cms = inject(CaseManagementService);
  ptSvc = inject(PostTradeService);
  dialogRef = inject<MatDialogRef<AttachmentsDialogComponent>>(MatDialogRef);
  private snacky = inject(MatSnackBar);
  private pltfrm = inject(Platform);
  private envSvc = inject(EnvironmentService);
  private ss = inject(SessionStorageService);
  private location = inject(Location);
  data = inject(MAT_DIALOG_DATA);

  baseUrl: string;
  environment;
  note = '';
  fileName;
  fileNameDisplay;
  file;
  fileType;
  caseFileCols = [
    {
      varName: 'CreateDate',
      label: 'Date',
    },
    {
      varName: 'NoteText',
      label: 'Note',
    },
    {
      varName: 'CreatedByUserId',
      label: 'Reviewer ID',
    },
    {
      varName: 'Name',
      label: 'Attachments',
    },
  ];
  relatedCases = [];
  tradeAttachments = [];
  rights = [];
  caseType;
  caseTitle;
  relatedCaseTypes;
  loading = false;

  ngOnInit() {
    this.environment = this.envSvc.get();
    this.baseUrl = this.environment.apiBase;
    this.rights = this.ss.get('rights') || [];
    if (this.data.bulk) {
      if (this.data.profileType === 'trade') {
        if (this.data.bulkAction === 'initiateCase') {
          if (this.data.bulkActionIds.length === 1) {
            this.caseType = 'Trade';
          } else {
            this.caseType = 'TradeGroup';
          }
        } else {
          this.caseType = 'TradeBulk';
        }
        this.caseTitle = this.data.tradeId;
      } else if (this.data.profileType === 'account') {
        if (this.data.bulkAction === 'initiateCase') {
          if (this.data.bulkActionIds.length === 1) {
            this.caseType = 'Account';
          } else {
            this.caseType = 'AccountGroup';
          }
        } else {
          this.caseType = 'AccountBulk';
        }
        this.caseTitle = this.data.AccountID;
      } else if (this.data.profileType === 'rep') {
        if (this.data.bulkAction === 'initiateCase') {
          if (this.data.bulkActionIds.length === 1) {
            this.caseType = 'Rep';
          } else {
            this.caseType = 'RepGroup';
          }
        } else {
          this.caseType = 'RepBulk';
        }
        this.caseTitle = this.data.RepID;
      } else if (this.data.profileType === 'aml') {
        if (this.data.bulkAction === 'initiateCase') {
          if (this.data.bulkActionIds.length === 1) {
            this.caseType = 'AML';
          } else {
            this.caseType = 'AMLGroup';
          }
        } else {
          this.caseType = 'AMLBulk';
        }
        this.caseTitle = this.data.alertID_Rev;
      }
    } else {
      if (this.data.profileType === 'trade') {
        if (
          this.data.bulkAction === 'initiateCase' ||
          this.data.bulkAction === 'addNote' ||
          this.data.bulkAction === undefined
        ) {
          this.caseType = 'TradeInternal';
        } else {
          this.caseType = 'Trade';
        }
        this.relatedCaseTypes = ['TradeInternal', 'TradeBulk'];
        this.caseTitle = this.data.tradeId;
      } else if (this.data.profileType === 'account') {
        if (
          this.data.bulkAction === 'initiateCase' ||
          this.data.bulkAction === 'addNote' ||
          this.data.bulkAction === undefined
        ) {
          this.caseType = 'AccountInternal';
        } else {
          this.caseType = 'Account';
        }
        this.relatedCaseTypes = ['AccountInternal', 'AccountBulk'];
        this.caseTitle = this.data.AccountID;
      } else if (this.data.profileType === 'rep') {
        if (
          this.data.bulkAction === 'initiateCase' ||
          this.data.bulkAction === 'addNote' ||
          this.data.bulkAction === undefined
        ) {
          this.caseType = 'RepInternal';
        } else {
          this.caseType = 'Rep';
        }
        this.relatedCaseTypes = ['RepInternal', 'RepBulk'];
        this.caseTitle = this.data.RepID;
      } else if (this.data.profileType === 'aml') {
        if (
          this.data.bulkAction === 'initiateCase' ||
          this.data.bulkAction === 'addNote' ||
          this.data.bulkAction === undefined
        ) {
          this.caseType = 'AMLInternal';
        } else {
          this.caseType = 'AML';
        }
        this.relatedCaseTypes = ['AMLInternal', 'AMLBulk'];
        this.caseTitle = this.data.alertID_Rev;
      }
    }

    if (!this.data.bulk && this.data.tradeId) {
      this.cms
        .getRelatedCases(this.data.tradeId, this.relatedCaseTypes, 'rb')
        .subscribe(resp => {
          if (resp['length'] > 0) {
            const cases = Object.keys(resp);
            const caseIds = [];

            cases.map(idx => {
              this.relatedCases.push(resp[idx]);
              caseIds.push(resp[idx].CRID);
            });

            this.cms
              .getCaseNotesAndFiles(JSON.stringify(caseIds))
              .subscribe(filesResp => {
                if (filesResp['length'] > 0) {
                  const formattedAttachments = [];
                  for (const index in filesResp) {
                    const relatedFile = formattedAttachments.find(
                      formattedAttachment =>
                        formattedAttachment.NoteId ===
                        filesResp[index]['NoteId']
                    );
                    if (!relatedFile) {
                      formattedAttachments.push(filesResp[index]);
                    } else {
                      const mergedFile = Object.assign(
                        relatedFile,
                        filesResp[index]
                      );
                      formattedAttachments.push(mergedFile);
                    }
                  }

                  this.tradeAttachments = uniqBy(
                    formattedAttachments,
                    'NoteId'
                  );
                }
              });
          }
        });
    }
  }

  onFileSelected(event) {
    this.file = event.target.files[0];

    if (this.file) {
      this.fileName = this.file.name;
    }
  }

  clearFile() {
    this.fileName = null;
    this.fileNameDisplay = null;
    this.file = null;
    this.fileType = null;
  }

  save() {
    if (this.data.bulk) {
      this.executeBulkAction();
    } else {
      const caseData = {
        CaseManagement: {
          Type: this.caseType,
          Title: this.data.tradeId,
        },
        CaseToProfile: {
          ProfileID: [this.data.tradeId],
        },
        Client_Rep: {
          RepCode: [this.data.tradeDetail.RepCode],
        },
      };
      if (
        this.relatedCases.length === 0 ||
        !this.relatedCases?.find(
          relatedCase => relatedCase.Type === 'TradeInternal'
        )
      ) {
        this.cms
          .createCaseManagementRecord(null, caseData)
          .subscribe(createCaseResp => {
            if (createCaseResp['result']) {
              this.saveNotesAndAttachments(createCaseResp['result']);
            }
          });
      } else {
        this.relatedCases.map(relatedCase => {
          if (relatedCase.Type === 'TradeInternal') {
            this.saveNotesAndAttachments(relatedCase.CRID);
          }
        });
      }
    }
  }

  executeBulkAction() {
    const hasAttachment = this.tradeAttachments.length > 0;
    const configurationObj = {
      CaseType: this.caseType,
      CaseTitle: this.caseTitle,
      CreateCase: true,
    };
    let idKey = 'TradeID',
      approvalVal = 'APP';

    if (this.data.profileType === 'account') {
      idKey = 'AccountID';
      approvalVal = 'REV';
    }

    if (this.data.profileType === 'rep') {
      idKey = 'RepID';
      approvalVal = 'REV';
    }

    if (this.data.profileType === 'aml') {
      idKey = 'AlertID_Rev';
      approvalVal = 'REV';
    }

    if (this.data.bulkAction === 'reopen') {
      approvalVal = 'REO';
    }

    if (this.data.bulkAction === 'inProcess') {
      approvalVal = 'INP';
    }

    if (this.data.bulkAction === 'reject') {
      approvalVal = 'REJ';
    }

    if (!this.data.bulk && hasAttachment) {
      delete configurationObj['CaseType'];
    }

    if (this.data.bulkAction !== 'initiateCase') {
      configurationObj['ApprovalStatus'] = approvalVal;
    }

    if (this.data.queryData) {
      configurationObj['UpdateQuery'] = this.data.queryData;
    }
    configurationObj[idKey] = this.data.bulkActionIds;

    if (this.data.bulkAction === 'addNote') {
      delete configurationObj['ApprovalStatus'];
    }

    if (this.data.profileType === 'trade') {
      this.updateTrade(configurationObj);
    } else if (this.data.profileType === 'account') {
      this.updateAccount(configurationObj);
    } else if (this.data.profileType === 'rep') {
      this.updateRep(configurationObj);
    } else if (this.data.profileType === 'aml') {
      this.updateAml(configurationObj);
    }
  }

  createEmptyCase(configData) {
    const casePayload = {
      CaseManagement: {
        Type: configData.CaseType,
        Title: `${this.data.profileType
          .toLowerCase()
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ')} ${DateTime.now().toUTC().toISO()}`,
      },
      CaseToProfile: {
        ProfileID: this.data.bulkActionIds,
      },
      Client_Rep: {
        RepCode: [this.ss.get('repUnits')[0].repCode],
      },
    };

    this.cms.createCaseManagementRecord(null, casePayload).subscribe(resp => {
      if (resp['result']) {
        this.saveNotesAndAttachments(resp['result']);
      }
    });
  }

  updateTrade(configData) {
    this.loading = true;
    this.ptSvc.updateTrade(configData).subscribe({
      next: (resp: TradeResponse) => {
        if (resp.crid && resp.result) {
          this.saveNotesAndAttachments(resp.crid, resp.result);
          if (this.data.bulkAction === 'initiateCase') {
            window.open(
              `${this.location.prepareExternalUrl(
                '/case-management/case-manager/'
              )}${resp.crid}`,
              '_blank'
            );
          }
        }
        this.loading = false;
        if (
          this.data.bulkAction === 'reopen' ||
          this.data.bulkAction === 'inProcess'
        ) {
          this.dialogRef.close();
          this.dialogRef = null;
        }
      },
      error: err => {
        this.openSnacky(`Error: ${err.errorMessage}`);
      },
    });
  }

  updateAccount(configData) {
    this.loading = true;
    this.ptSvc.updateAccount(configData).subscribe({
      next: (resp: TradeResponse) => {
        if (resp.crid && resp.result) {
          this.saveNotesAndAttachments(resp.crid, resp.result);
          if (this.data.bulkAction === 'initiateCase') {
            window.open(
              `${this.location.prepareExternalUrl(
                '/case-management/case-manager/'
              )}${resp.crid}`,
              '_blank'
            );
          }
        }
        this.loading = false;
        if (
          this.data.bulkAction === 'reopen' ||
          this.data.bulkAction === 'inProcess'
        ) {
          this.dialogRef.close();
          this.dialogRef = null;
        }
      },
      error: err => {
        this.openSnacky(`Error: ${err.errorMessage}`);
      },
    });
  }

  updateRep(configData) {
    this.loading = true;
    this.ptSvc.updateRep(configData).subscribe({
      next: (resp: TradeResponse) => {
        if (resp.crid && resp.result) {
          this.saveNotesAndAttachments(resp.crid, resp.result);
          if (this.data.bulkAction === 'initiateCase') {
            window.open(
              `${this.location.prepareExternalUrl(
                '/case-management/case-manager/'
              )}${resp.crid}`,
              '_blank'
            );
          }
        }
        this.loading = false;
        if (
          this.data.bulkAction === 'reopen' ||
          this.data.bulkAction === 'inProcess'
        ) {
          this.dialogRef.close();
          this.dialogRef = null;
        }
      },
      error: err => {
        this.openSnacky(`Error: ${err.errorMessage}`);
      },
    });
  }

  updateAml(configData) {
    this.loading = true;
    this.ptSvc.updateAmlRecord(configData).subscribe({
      next: (resp: AccountResult) => {
        if (resp.crid && resp.result) {
          this.saveNotesAndAttachments(resp.crid, resp.result);
          if (this.data.bulkAction === 'initiateCase') {
            window.open(
              `${this.location.prepareExternalUrl(
                '/case-management/case-manager/'
              )}${resp.crid}`,
              '_blank'
            );
          }
        }
        this.loading = false;
        if (
          this.data.bulkAction === 'reopen' ||
          this.data.bulkAction === 'inProcess'
        ) {
          this.dialogRef.close();
          this.dialogRef = null;
        }
      },
      error: err => {
        this.openSnacky(`Error: ${err.errorMessage}`);
      },
    });
  }

  saveNotesAndAttachments(caseId, idList?) {
    if (this.note !== '' && this.note !== null && !this.fileName) {
      const note = {
        CaseRecordId: caseId,
        NoteText: this.note,
        NoteTypeId: 2,
      };

      this.cms.addCaseRecordNote(note).subscribe({
        next: () => {
          this.note = '';
          this.openSnacky('Note saved');
          this.dialogRef.close();
          this.dialogRef = null;
        },
        error: error => {
          this.snacky.open(`Error: ${error}`);
        },
      });
    } else if (this.fileName && (this.note === '' || this.note === null)) {
      const formData = new FormData();
      formData.append('File1', this.file);
      this.fileName = '';

      this.cms.saveCaseRecordFile(caseId, formData).subscribe({
        next: () => {
          this.openSnacky('File uploaded.');
          this.dialogRef.close();
          this.dialogRef = null;
        },
        error: error => {
          this.snacky.open(`Error: ${error}`);
        },
      });
    } else if (this.data.bulkAction === 'initiateCase' && this.data.bulk) {
      let newNoteText = `A case has been initiated for the following ${this.data.profileType}s. Please review the details and provide any necessary notes or actions.<br/><br/>~`;
      if (this.data.bulkActionIds && this.data.bulkActionIds.length > 0) {
        this.data.bulkActionIds.map((tradeId, index) => {
          newNoteText =
            newNoteText +
            `${tradeId}${index < this.data.bulkActionIds.length - 1 ? '|' : ''}`;
        });
      } else if (idList && idList.length > 0) {
        idList.map((tradeId, index) => {
          newNoteText =
            newNoteText + `${tradeId}${index < tradeId.length - 1 ? '|' : ''}`;
        });
      }
      this.note = newNoteText;

      this.cms
        .addCaseRecordNote({
          CaseRecordId: caseId,
          NoteText: this.note,
          NoteTypeId: 3,
        })
        .subscribe({
          next: () => {
            this.note = '';
            this.openSnacky('Note saved.');
            this.dialogRef.close();
            this.dialogRef = null;
          },
          error: error => {
            this.snacky.open(`Error: ${error}`);
          },
        });
    } else {
      const formData = new FormData();
      formData.append('File1', this.file);
      this.fileName = '';
      this.cms.saveCaseRecordFile(caseId, formData).subscribe({
        next: resp => {
          if (resp['id']) {
            this.cms
              .addCaseRecordNote({
                CaseRecordId: caseId,
                CaseRecordFileId: resp['id'],
                NoteText: this.note,
                NoteTypeId: 2,
              })
              .subscribe({
                next: () => {
                  this.note = '';
                  this.openSnacky('File uploaded & note saved.');
                  this.dialogRef.close();
                  this.dialogRef = null;
                },
                error: error => {
                  this.openSnacky(`Error: ${error}`);
                },
              });
          }
        },
        error: error => {
          this.openSnacky(`Error: ${error}`);
        },
      });
    }
  }

  openSnacky(message) {
    this.snacky.open(message, 'Close', {
      duration: 3000,
    });
  }

  downloadAttachment(fileDetails) {
    this.cms.getCaseRecordFile(fileDetails.id).subscribe(resp => {
      const filename = `${fileDetails.Name}`;
      const iosSafari =
        this.pltfrm.IOS ||
        (navigator?.['userAgentData']?.platform === 'macOS' &&
          navigator.maxTouchPoints > 1);
      let fileData;
      const reader = new FileReader();
      reader.readAsDataURL(resp);
      reader.onloadend = () => {
        fileData = reader.result;
        saveAs(fileData, filename, {
          proxyURL: `${this.baseUrl}/util/proxy`,
          proxyTarget: '_self',
          forceProxy: iosSafari,
        });
      };
    });
  }

  cancel() {
    this.dialogRef.close('cancel');
    this.dialogRef.afterClosed().subscribe(() => {
      this.dialogRef = null;
    });
  }
}
